import React, { useState, ChangeEvent, FormEvent } from "react";
import {
  ConversionRequest,
  OutputType,
  ModelType,
  MachineType,
  ConversionParameters,
} from "../../../types/conversionTypes";
import { MACHINE_CONSTANTS } from "../../../constants/machineConstants";
import  {Card, Button, FileDropArea}  from "../../common";

//#region 型定義
interface ConversionFormProps {
  /** 変換実行時のコールバック */
  onSubmit: (request: ConversionRequest) => void;
  /** ファイル選択時のコールバック */
  onFileSelect: (file: File | null) => void;
  /** リセット時のコールバック */
  onReset: () => void;
  /** ローディング状態 */
  isLoading: boolean;
}

/** フォームの初期状態の型 */
type FormState = ConversionRequest;
//#endregion

/**
 * 変換フォームコンポーネント
 * ファイル変換のためのフォームを提供し、入力検証を管理する
 */
export const ConversionForm: React.FC<ConversionFormProps> = ({
  onSubmit,
  onFileSelect,
  onReset,
  isLoading,
}) => {
  //#region ステート定義
  /** 初期状態の設定 */
  const initialState: FormState = {
    inputFilePath: "",
    conversionParameters: {
      mdlType: "FUNAC",
      outType: "DXF",
      machineToolType: "ミル",
      contourLine_DispMode: false,
      subProgram_OutMode: false,
      outputFilePath: "",
    },
  };

  /** フォームデータの状態 */
  const [formData, setFormData] = useState<FormState>(initialState);
  /** ファイルエラーメッセージ */
  const [fileError, setFileError] = useState<string | null>(null);
  /** 選択されたファイル */
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  //#endregion

  //#region フラグ管理
  /** 条件付き表示制御のフラグ */
  const shouldShowContourLine = formData.conversionParameters.outType === "DXF";
  const shouldShowSubProgram = formData.conversionParameters.outType === "NC";
  //#endregion

  //#region ファイル処理
  /**
   * ファイルの検証と設定を行う
   * @param file 処理対象のファイル
   * @returns 処理が成功したかどうか
   */
  const processFile = (file: File): boolean => {
    if (!file.name.toLowerCase().endsWith(".nc")) {
      setFileError("NCファイル(.nc)を選択してください");
      setSelectedFile(null);
      onFileSelect(null);
      return false;
    }

    const inputPath = `C:\\Temp\\Input\\${file.name}`;
    const outputPath = `C:\\Temp\\Output\\${file.name.replace(".nc", ".dxf")}`;

    setSelectedFile(file);
    onFileSelect(file);

    setFormData((prev: FormState) => ({
      ...prev,
      inputFilePath: inputPath,
      conversionParameters: {
        ...prev.conversionParameters,
        outputFilePath: outputPath,
        outType: "DXF",
        mdlType: prev.conversionParameters.mdlType,
        machineToolType: prev.conversionParameters.machineToolType,
      },
    }));

    setFileError(null);
    return true;
  };
  //#endregion

  //#region イベントハンドラー
  /**
   * ファイルドロップの処理
   * @param files ドロップされたファイルリスト
   */
  const handleFileDrop = (files: FileList) => {
    const file = files[0];
    if (file) {
      processFile(file);
    }
  };

  /**
   * フォームをリセットする
   */
  const handleReset = () => {
    setFormData(initialState);
    setSelectedFile(null);
    setFileError(null);
    onFileSelect(null);
    onReset();

    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  /**
   * 入力値の変更を処理する
   * @param e 変更イベント
   */
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (name.includes(".")) {
      const [, child] = name.split(".");
      setFormData((prev: FormState) => {
        const updatedParams = {
          ...prev.conversionParameters,
          [child]:
            type === "checkbox"
              ? (e.target as HTMLInputElement).checked
              : value,
        } as ConversionParameters;

        if (child === "outType") {
          const newOutType = value as OutputType;
          if (newOutType !== "DXF") {
            updatedParams.contourLine_DispMode = false;
          }
          if (newOutType !== "NC") {
            updatedParams.subProgram_OutMode = false;
          }

          if (selectedFile) {
            const extension =
              newOutType === "レポート" ? ".log" : `.${value.toLowerCase()}`;
            updatedParams.outputFilePath = `C:\\Temp\\Output\\${selectedFile.name.replace(
              ".nc",
              extension
            )}`;
          }
        }

        return {
          ...prev,
          conversionParameters: updatedParams,
        };
      });
    }
  };

  /**
   * フォーム送信を処理する
   * @param e 送信イベント
   */
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedFile) {
      setFileError("ファイルが選択されていません");
      return;
    }

    if (!formData.inputFilePath) {
      setFileError("入力ファイルパスは必須です");
      return;
    }

    onSubmit(formData);
  };
  //#endregion

  //#region レンダリング
  return (
    <Card shadow="sm" className="bg-white">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ファイル選択 */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            入力ファイル (NC)
          </label>
          <div className="max-w-sm mx-auto">
            <FileDropArea onDrop={handleFileDrop} error={fileError} />
          </div>
        </div>

        {/* 変換設定 */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="max-w-sm mx-auto space-y-3">
            {/* モデルタイプ */}
            <div>
              <label
                htmlFor="mdlType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                モデルタイプ
              </label>
              <select
                id="mdlType"
                name="conversionParameters.mdlType"
                value={formData.conversionParameters.mdlType}
                onChange={handleInputChange}
                className="block w-full h-9 px-3 rounded-md border-gray-300 
                         bg-white focus:border-blue-500 focus:ring-blue-500 
                         text-sm shadow-sm"
              >
                {MACHINE_CONSTANTS.MODEL_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 出力タイプ */}
            <div>
              <label
                htmlFor="outType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                出力タイプ
              </label>
              <select
                id="outType"
                name="conversionParameters.outType"
                value={formData.conversionParameters.outType}
                onChange={handleInputChange}
                className="block w-full h-9 px-3 rounded-md border-gray-300 
                         bg-white focus:border-blue-500 focus:ring-blue-500 
                         text-sm shadow-sm"
              >
                {(
                  Object.entries(MACHINE_CONSTANTS.OUTPUT_TYPES) as [
                    string,
                    OutputType,
                  ][]
                ).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            {/* 工作機械タイプ */}
            <div>
              <label
                htmlFor="machineToolType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                工作機械タイプ
              </label>
              <select
                id="machineToolType"
                name="conversionParameters.machineToolType"
                value={formData.conversionParameters.machineToolType}
                onChange={handleInputChange}
                className="block w-full h-9 px-3 rounded-md border-gray-300 
                         bg-white focus:border-blue-500 focus:ring-blue-500 
                         text-sm shadow-sm"
              >
                {Object.values(MACHINE_CONSTANTS.MACHINE_TYPES).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* オプション設定 */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="max-w-sm mx-auto">
            <div className="flex flex-col gap-3">
              {shouldShowContourLine && (
                <div className="flex items-center">
                  <input
                    id="contourLineDispMode"
                    type="checkbox"
                    name="conversionParameters.contourLine_DispMode"
                    checked={formData.conversionParameters.contourLine_DispMode}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                             border-gray-300 rounded"
                  />
                  <label
                    htmlFor="contourLineDispMode"
                    className="ml-2 text-sm text-gray-700"
                  >
                    輪郭線表示を出力する
                  </label>
                </div>
              )}

              {shouldShowSubProgram && (
                <div className="flex items-center">
                  <input
                    id="subProgramOutMode"
                    type="checkbox"
                    name="conversionParameters.subProgram_OutMode"
                    checked={formData.conversionParameters.subProgram_OutMode}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                             border-gray-300 rounded"
                  />
                  <label
                    htmlFor="subProgramOutMode"
                    className="ml-2 text-sm text-gray-700"
                  >
                    サブプロをメイン展開して出力する
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ボタングループ */}
        <div className="flex justify-center gap-4 pt-4">
          <Button
            type="submit"
            variant="primary"
            size="medium"
            isLoading={isLoading}
            className="w-40 h-9 text-sm"
            disabled={!selectedFile || isLoading}
          >
            {isLoading ? "変換中..." : "変換開始"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="medium"
            className="w-40 h-9 text-sm"
            onClick={handleReset}
            disabled={isLoading}
          >
            リセット
          </Button>
        </div>
      </form>
    </Card>
  );
  //#endregion
};

export default ConversionForm;
